/**
 * Icon tokens (DESIGN_SYSTEM.md §8, M6 T-54). Pure: no React, so it is unit-tested.
 *
 * One stroke width for every lucide glyph, and one size scale. 40 / 48 are for
 * empty and error states only. `lg` (18) is the dialog-close / composer size that
 * the product already uses widely; it is on the scale on purpose.
 */
export const ICON_STROKE = 1.75;
/** The 40 / 48 empty-state glyphs draw lighter (optical size), as StateBlock and FocusList already did. */
export const ICON_STROKE_STATE = 1.5;

export const ICON_SIZE = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  /** Empty / error states only. */
  state: 40,
  'state-lg': 48,
} as const;

export type IconSizeToken = keyof typeof ICON_SIZE;
export type IconPx = (typeof ICON_SIZE)[IconSizeToken];

/** Every allowed pixel size, ascending. */
export const ICON_SCALE: readonly IconPx[] = Object.values(ICON_SIZE).sort((a, b) => a - b);

/** Sizes for inline UI (everything except the empty-state sizes). */
const INLINE_SCALE = ICON_SCALE.filter((px) => px <= ICON_SIZE['2xl']);

export function iconPx(size: IconSizeToken | IconPx): IconPx {
  return typeof size === 'number' ? size : ICON_SIZE[size];
}

/** The stroke for a size: 1.75 inline, 1.5 for the empty-state sizes. */
export function iconStroke(px: number): number {
  return px >= ICON_SIZE.state ? ICON_STROKE_STATE : ICON_STROKE;
}

export function isIconPx(px: number): px is IconPx {
  return (ICON_SCALE as readonly number[]).includes(px);
}

/**
 * The nearest allowed size for an off-scale value (the T-54 codemod rule):
 * up to 24 it snaps to the nearest inline step, and a tie goes toward the
 * default `md` 16 (13 → 14, 15 → 16, 22 → 20); above 24 it snaps to the
 * empty-state sizes (32 → 40).
 */
export function snapIconPx(px: number): IconPx {
  if (isIconPx(px)) return px;
  const pool = px > ICON_SIZE['2xl'] ? ICON_SCALE.filter((s) => s >= ICON_SIZE.state) : INLINE_SCALE;
  let best = pool[0];
  for (const s of pool) {
    const d = Math.abs(s - px);
    const bd = Math.abs(best - px);
    if (d < bd || (d === bd && Math.abs(s - ICON_SIZE.md) < Math.abs(best - ICON_SIZE.md))) best = s;
  }
  return best;
}
